import React, { useEffect, useState } from "react";
import { User } from "../../../models/users";
import { toast } from "react-hot-toast";
import { updateUserByEmail } from "../../../api/users";
import { sendEmailtoUser, sendSupportEmail } from "../../../api/mail";

interface EditUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    onUserUpdated: (updatedUser: User) => void;
}

export default function EditUserModal({
    isOpen,
    onClose,
    user,
    onUserUpdated,
}: EditUserModalProps) {
    const [form, setForm] = useState({
        name: user.name || "",
        password: "",
        role: user.role || "user",
    });

    const [editFlags, setEditFlags] = useState({
        name: false,
        password: false,
        role: false,
    });
    useEffect(() => {
        if (isOpen && user) {
            setForm({
                name: user.name || "",
                password: "",
                role: user.role || "user",
            });
            setEditFlags({ name: false, password: false, role: false });
        }
    }, [isOpen, user]);

    if (!isOpen) return null;

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => setForm({ ...form, [e.target.name]: e.target.value });

    const toggleField = (field: keyof typeof editFlags) => {
        setEditFlags({ ...editFlags, [field]: !editFlags[field] });
    };

    const handleConfirm = async () => {
        try {
            const payload: any = { email: user.email };
            if (editFlags.name) payload.name = form.name;
            if (editFlags.password) payload.password = form.password;
            if (editFlags.role) payload.role = form.role;

            if (Object.keys(payload).length === 1) {
                toast.error("Select at least one field to update");
                return;
            }

            const updated = await updateUserByEmail(payload);
            onUserUpdated(updated);
            toast.success("User updated successfully");
            onClose();
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to update user");
        }
    };

    const handleNotify = async () => {
        try {
            let body = `
<!DOCTYPE html>
<html lang="en" dir="ltr">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>RPM Credentials</title>
<style>
  a.es-button{text-decoration:none!important}
  .adapt-img{width:100%;height:auto}
  @media only screen and (max-width:600px){
    .es-left,.es-right,.es-content-body{width:100%!important;display:block!important}
  }
</style>
</head>

<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#fafafa">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#fafafa">
<tr><td align="center">

<table width="600" cellpadding="0" cellspacing="0" style="background:#fff;width:600px">
<tr>
  <td style="background:#666;padding:20px;text-align:center">
    <h2 style="color:#ed6b20;font-size:46px;margin:0;font-weight:bold">RPM MARKETPLACE</h2>
    <h2 style="color:#fff;font-size:46px;margin:0;font-weight:bold">LOGIN CREDENTIALS</h2>
  </td>
</tr>

<tr>
<td style="background:#666;padding:20px;color:#fff">

  <table align="left" width="173" cellpadding="0" cellspacing="0">
    <tr>
      <td style="font-size:0">
        <a href="https://marketplace.rpmleader.com" target="_blank">
          <img class="adapt-img" src="https://marketplace.rpmleader.com/media/thumbnail_marketplace.png" width="173" style="display:block;border:0" />
        </a>
      </td>
    </tr>
  </table>

  <table align="right" width="350" cellpadding="0" cellspacing="0" style="color:#fff;font-size:15px;line-height:22px">
    <tr><td>

    ${editFlags.name ? `<p>Hello ${form.name},</p>` : `<p>Hello ${user.name},</p>`}
      <p>Here are your <strong style="color:#ed6b20">RPM Marketplace</strong> credentials:</p>

     <div style="padding-left:10px">
 <p style="margin:0;line-height:20px">Username: <strong style="color:#ed6b20">${user.email}</strong></p>
  ${editFlags.password ? `<p style="margin:0;line-height:20px">Password: <strong style="color:#ed6b20">${form.password}</strong></p>` : ""}</div >
    <p>Marketplace: <a href="https://marketplace.rpmleader.com" style="color:#ed6b20; text-decoration:underline">marketplace.rpmleader.com</a>
    </p>
      <p>Please do not share your credentials with anyone.</p>
      <p>If you face any issue or want to change your password, contact <strong style="color:#ed6b20">tech@rpmleader.com</strong>.</p>

      <p>Thanks,<br/>RPM Tech Team</p>

    </td ></tr >
  </table >

</td >
</tr >

</table >

</td ></tr >
</table >
</body >
</html >
        `
            await sendEmailtoUser({
                subject: "RPM Marketplace - Login Credentials",
                body,
                receiving_email: user.email,
            });

            toast.success(`Notification sent to ${user.email} `);
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to notify user");
        }
    };


    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-96 shadow-lg">
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                    Editing {user.email}
                </h2>

                {/* Name */}
                <div className="mb-3 flex items-center space-x-2">
                    <input
                        type="checkbox"
                        checked={editFlags.name}
                        onChange={() => toggleField("name")}
                    />
                    <input
                        type="text"
                        name="name"
                        placeholder="Full name"
                        value={form.name}
                        onChange={handleChange}
                        disabled={!editFlags.name}
                        className="flex-1 p-2 border rounded dark:bg-gray-700 dark:text-white"
                    />
                </div>

                {/* Password */}
                <div className="mb-3 flex items-center space-x-2">
                    <input
                        type="checkbox"
                        checked={editFlags.password}
                        onChange={() => toggleField("password")}
                    />
                    <input
                        type="password"
                        name="password"
                        placeholder="6-50 characters"
                        value={form.password}
                        onChange={handleChange}
                        disabled={!editFlags.password}
                        className="flex-1 p-2 border rounded dark:bg-gray-700 dark:text-white"
                    />
                </div>

                {/* Role */}
                <div className="mb-4 flex items-center space-x-2">
                    <input
                        type="checkbox"
                        checked={editFlags.role}
                        onChange={() => toggleField("role")}
                    />
                    <select
                        name="role"
                        value={form.role}
                        onChange={handleChange}
                        disabled={!editFlags.role}
                        className="flex-1 p-2 border rounded dark:bg-gray-700 dark:text-white"
                    >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="superadmin">Super Admin</option>
                    </select>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="px-4 py-2 rounded text-white bg-blue-600 hover:bg-blue-700"
                    >
                        Save
                    </button>
                    <button
                        onClick={handleNotify}
                        className="px-4 py-2 rounded text-white bg-green-600 hover:bg-green-700"
                    >
                        Notify User
                    </button>
                </div>
            </div>
        </div>
    );
}
